import * as React from "react";
import { debounce } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import { useCustomSearchParams } from "../../../hooks/useCustomSearchParams";
import { useResponsive } from "../../../hooks/useResponsive";
import { searchCard } from "../../../redux/SearchSlice";
import { useWatching } from "../../../reactQuery/users";
import {
  useGenericWorkflowStatus,
  useUserWorkflowList,
} from "../../../reactQuery/workflows";
import {
  filterWatchingProjects,
  getSortedWatchingWorkflowSteps,
  getWatchingWorkflowStatus,
  mapWatchingCardDetailItems,
} from "../utils/watchingUtils";

export function useWatchingPageState() {
  const [searchParams] = useSearchParams();
  const [searchParam, setSearchParam, removeSearchParam] =
    useCustomSearchParams("txt");
  const dispatch = useDispatch();
  const isMobile = useResponsive("down", "md");
  const epicSearch = useSelector((state) => state.epic.searchEpic);
  const searchText = useSelector((state) => state.search.searchText);
  const gridView = useSelector((state) => state.search.gridView);
  const cardId = searchParams.get("card_id");
  const filterUser = searchParams.get("search");

  const { genericWorkflowStatus: workflows } = useGenericWorkflowStatus();
  const { userWorkflows } = useUserWorkflowList();
  const genericWorkflowId = userWorkflows?.data?.data?.responseData?.find(
    (workflow) => workflow.is_generic
  )?.workflow_id;
  const { data, isLoading, isRefetching } = useWatching(genericWorkflowId);

  const [inputValue, setInputValue] = React.useState(searchParam || "");

  const debouncedSearch = React.useRef(
    debounce((value) => {
      dispatch(searchCard(value));
      if (value) {
        setSearchParam("txt", value);
      } else {
        removeSearchParam("txt");
      }
    }, 300)
  ).current;

  React.useEffect(() => {
    setInputValue(searchParam || "");
    dispatch(searchCard(searchParam || ""));
  }, [dispatch, searchParam]);

  React.useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleChange = React.useCallback(
    (event) => {
      const value = event?.target?.value?.trimStart();
      setInputValue(value);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  const projects = React.useMemo(
    () =>
      filterWatchingProjects(data?.data?.data?.responseData || [], {
        searchText,
        filterUser,
        epicSearch,
      }),
    [data, epicSearch, filterUser, searchText]
  );

  const workflowSteps = React.useMemo(
    () => getSortedWatchingWorkflowSteps(workflows),
    [workflows]
  );

  const getCardStatus = React.useCallback(
    (workflowStepId) =>
      getWatchingWorkflowStatus(
        workflows?.data?.data?.responseData || [],
        workflowStepId
      ),
    [workflows]
  );

  const cardDetailItems = React.useMemo(
    () => mapWatchingCardDetailItems(projects),
    [projects]
  );

  return {
    data,
    isLoading,
    isRefetching,
    isMobile,
    gridView,
    workflows,
    workflowSteps,
    projects,
    cardId,
    inputValue,
    handleChange,
    getCardStatus,
    cardDetailItems,
  };
}
